// components/widgets/dashboard-grid.js
// ═══════════════════════════════════════════════════════════
// DASHBOARD GRID — row-based smart layout
// ═══════════════════════════════════════════════════════════
// Renders a vertical stack of WidgetRow components. Wraps
// everything in DndContext so widgets can be dragged between
// rows via drop zones.
// ═══════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import WidgetRow from './widget-row';
import { getWidget } from '../../lib/widgets/registry';
import { findWidget, MAX_WIDGETS_PER_ROW } from '../../lib/widgets/rows-model';

export default function DashboardGrid({
  rows = [],
  workstation,
  userId,
  userAiName = null,
  editing = false,
  onRemoveWidget,
  onAdjustShare,
  onDragEnd,
}) {
  const [activeWidgetKey, setActiveWidgetKey] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback((event) => {
    setActiveWidgetKey(event.active?.id || null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveWidgetKey(null);
    const activeId = event.active?.id;
    const overId = event.over?.id;
    if (!activeId) return;
    if (!overId || activeId === overId) return;

    if (typeof overId === 'string' && overId.startsWith('row_gap_')) {
      const idx = parseInt(overId.replace('row_gap_', ''), 10);
      if (Number.isFinite(idx) && onDragEnd) {
        onDragEnd({ type: 'move_to_new_row', widgetKey: activeId, insertRowIndex: idx });
      }
      return;
    }

    const srcLoc = findWidget(rows, activeId);
    const dstLoc = findWidget(rows, overId);
    if (!srcLoc || !dstLoc) return;

    const srcRow = rows[srcLoc.rowIndex];
    const dstRow = rows[dstLoc.rowIndex];

    if (srcRow.id === dstRow.id) {
      if (onDragEnd) {
        const newWidgets = [...srcRow.widgets];
        const [moved] = newWidgets.splice(srcLoc.widgetIndex, 1);
        newWidgets.splice(dstLoc.widgetIndex, 0, moved);
        const newRows = rows.map((r) =>
          r.id === srcRow.id ? { ...r, widgets: newWidgets } : r
        );
        onDragEnd({ type: 'save_rows', rows: newRows });
      }
      return;
    }

    if (dstRow.widgets.length >= MAX_WIDGETS_PER_ROW) {
      if (onDragEnd) {
        onDragEnd({ type: 'move_to_new_row', widgetKey: activeId, insertRowIndex: dstLoc.rowIndex });
      }
      return;
    }
    if (onDragEnd) {
      onDragEnd({
        type: 'insert_in_row',
        widgetKey: activeId,
        rowId: dstRow.id,
        position: dstLoc.widgetIndex,
      });
    }
  }, [rows, onDragEnd]);

  const handleDragCancel = useCallback(() => setActiveWidgetKey(null), []);

  if (!rows.length) {
    return (
      <div className="ws-dashboard-empty">
        <div className="ws-dashboard-empty-eyebrow">No widgets</div>
        <div className="ws-dashboard-empty-title">This dashboard is empty</div>
        <div className="ws-dashboard-empty-body">
          Click ✦ Customize to add widgets from the library.
        </div>
      </div>
    );
  }

  const overlayWidget = activeWidgetKey ? getWidget(activeWidgetKey) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={`ws-dashboard-rows ${editing ? 'is-editing' : ''}`}>
        {editing && <RowGapDropZone index={0} />}
        {rows.map((row, rowIdx) => (
          <div key={row.id} className="ws-dashboard-row-wrap">
            <WidgetRow
              row={row}
              workstation={workstation}
              userId={userId}
              userAiName={userAiName}
              editing={editing}
              onRemoveWidget={onRemoveWidget}
              onAdjustShare={onAdjustShare}
            />
            {editing && <RowGapDropZone index={rowIdx + 1} />}
          </div>
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeWidgetKey && overlayWidget && (
          <div className="ws-card ws-card-drag-preview" data-widget={activeWidgetKey}>
            <div className="ws-card-header">
              <div className="ws-card-header-left">
                <span className="ws-card-glyph" data-tier={overlayWidget.tier} aria-hidden>⋮⋮</span>
                <h3 className="ws-card-title" data-tier={overlayWidget.tier}>{overlayWidget.name}</h3>
              </div>
            </div>
            <div className="ws-card-drag-body">Moving…</div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function RowGapDropZone({ index }) {
  const id = `row_gap_${index}`;
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`ws-dashboard-row-gap ${isOver ? 'is-over' : ''}`}
      aria-hidden
    >
      <div className="ws-dashboard-row-gap-line" />
    </div>
  );
}
